import { useEffect, useState } from "react";
import { Button } from "../../design/button";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "../../design/dialog";
import { Input } from "../../design/input";
import {
  Field,
  Fieldset,
  FieldGroup,
  Label,
  ErrorMessage,
} from "../../design/fieldset";
import { Textarea } from "../../design/textarea";
import { Select } from "../../design/select";
import { PlusIcon } from "@heroicons/react/20/solid";
import { useParticipant } from "../../contexts/ParticipantContext";
import { useHolidaySchedule } from "../../contexts/HolidayScheduleContext";
import ChooseHolidayStep from "./components/ChooseHolidayStep";
import {
  HolidayAssignmentMode,
  holidayAssignmentModes,
} from "../../models/HolidayAssignmentMode";
import moment from "moment";
import { Holiday } from "../../models/Holiday";
import { Participant } from "../../models/Participant";
import { HolidaySchedule } from "../../models/HolidaySchedule";

interface Props {
  holidayScheduleId?: HolidaySchedule["_id"];
  children?: React.ReactNode;
  className?: string;
}

const CreateHolidayRuleButton: React.FC<Props> = ({
  holidayScheduleId,
  children,
  className,
}) => {
  const { participants } = useParticipant();
  const { createHolidayRule } = useHolidaySchedule();

  const [isOpen, setIsOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [holidaySource, setHolidaySource] = useState<"existing" | "custom">();
  const [selectedHoliday, setSelectedHoliday] = useState<Holiday | null>(null);

  const [name, setName] = useState("");
  const [date, setDate] = useState(moment().format("YYYY-MM-DD"));
  const [notes, setNotes] = useState("");
  const [assignmentMode, setAssignmentMode] =
    useState<HolidayAssignmentMode>("every");
  const [everyYearParticipant, setEveryYearParticipant] =
    useState<Participant["_id"]>();
  const [evenYearsParticipant, setEvenYearsParticipant] =
    useState<Participant["_id"]>();
  const [oddYearsParticipant, setOddYearsParticipant] =
    useState<Participant["_id"]>();

  const [startDateTimeStr, setStartDateTimeStr] = useState("");
  const [endDateTimeStr, setEndDateTimeStr] = useState("");
  const [errors, setErrors] = useState<{ name?: string }>({});

  useEffect(() => {
    if (holidaySource === "custom") {
      const anchor = moment(date).startOf("day");
      setStartDateTimeStr(
        anchor.clone().add(9, "hours").format("YYYY-MM-DDTHH:mm"),
      );
      setEndDateTimeStr(
        anchor.clone().add(18, "hours").format("YYYY-MM-DDTHH:mm"),
      );
    }
  }, [date, holidaySource]);

  const validateForm = () => {
    const newErrors: { name?: string } = {};
    if (!name.trim()) newErrors.name = "Name is required.";
    if (moment(startDateTimeStr).isAfter(moment(endDateTimeStr))) {
      newErrors.name = "Start must be before End.";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    setIsSaving(true);

    const anchor = moment(
      selectedHoliday
        ? (selectedHoliday.dates.find((d) =>
            d.startsWith(moment().year().toString()),
          ) ?? selectedHoliday.dates[0])
        : date,
    ).startOf("day");

    const startOffsetMinutes = moment(startDateTimeStr).diff(anchor, "minutes");
    const endOffsetMinutes = moment(endDateTimeStr).diff(anchor, "minutes");

    try {
      await createHolidayRule(holidayScheduleId!, {
        name,
        notes,
        startOffsetMinutes,
        endOffsetMinutes,
        everyYearParticipant:
          assignmentMode === "every" ? everyYearParticipant : undefined,
        evenYearsParticipant:
          assignmentMode === "alternate" ? evenYearsParticipant : undefined,
        oddYearsParticipant:
          assignmentMode === "alternate" ? oddYearsParticipant : undefined,
        assignmentMode,
        holiday: selectedHoliday?._id,
        date: selectedHoliday ? undefined : date,
      });
      setIsOpen(false);
    } catch (err: any) {
      setErrors((prev) => ({ ...prev, name: err.message }));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <Button
        type="button"
        className={className}
        onClick={() => setIsOpen(true)}
      >
        {children ?? (
          <div className="flex items-center gap-2">
            <PlusIcon className="w-4 h-4" />
            Create Holiday Rule
          </div>
        )}
      </Button>

      <Dialog open={isOpen} onClose={setIsOpen}>
        <DialogTitle>Create Holiday Rule</DialogTitle>
        {step === 1 && <DialogBody>How would you like to start?</DialogBody>}
        <DialogBody>
          {step === 1 && (
            <Fieldset>
              <div className="flex flex-col gap-2">
                <Button
                  onClick={() => {
                    setHolidaySource("existing");
                    setStep(2);
                  }}
                >
                  Choose from Existing Holidays
                </Button>
                <Button
                  onClick={() => {
                    setHolidaySource("custom");
                    setStep(2);
                  }}
                >
                  Create a Custom Holiday
                </Button>
              </div>
            </Fieldset>
          )}

          {step === 2 && holidaySource === "existing" && (
            <ChooseHolidayStep
              onSelect={(holiday) => {
                setSelectedHoliday(holiday);
                setName(holiday.name);
                const baseDate = holiday.dates[0];
                setStartDateTimeStr(
                  moment(baseDate)
                    .add(holiday.startOffsetMinutes ?? 540, "minutes")
                    .format("YYYY-MM-DDTHH:mm"),
                );
                setEndDateTimeStr(
                  moment(baseDate)
                    .add(holiday.endOffsetMinutes ?? 1080, "minutes")
                    .format("YYYY-MM-DDTHH:mm"),
                );
                setAssignmentMode(holiday.assignmentMode ?? "alternate");
                setStep(3);
              }}
            />
          )}

          {step === 2 && holidaySource === "custom" && (
            <Fieldset>
              <Field>
                <Label>Holiday Name</Label>
                <Input
                  value={name}
                  placeholder="e.g. Grandparents Weekend"
                  onChange={(e) => setName(e.target.value)}
                />
              </Field>
              <Field>
                <Label>Date</Label>
                <Input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                />
              </Field>
            </Fieldset>
          )}

          {step === 3 && (
            <Fieldset>
              <FieldGroup>
                <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 sm:gap-4">
                  <Field>
                    <Label>Start Date</Label>
                    <Input
                      type="datetime-local"
                      value={startDateTimeStr}
                      onChange={(e) => setStartDateTimeStr(e.target.value)}
                    />
                  </Field>
                  <Field>
                    <Label>End Date</Label>
                    <Input
                      type="datetime-local"
                      value={endDateTimeStr}
                      onChange={(e) => setEndDateTimeStr(e.target.value)}
                    />
                  </Field>
                </div>

                <Field>
                  <Label>Assignment Mode</Label>
                  <Select
                    value={assignmentMode}
                    onChange={(e) =>
                      setAssignmentMode(e.target.value as HolidayAssignmentMode)
                    }
                  >
                    {Object.entries(holidayAssignmentModes).map(
                      ([key, value]) => (
                        <option key={key} value={key}>
                          {value}
                        </option>
                      ),
                    )}
                  </Select>
                </Field>

                {assignmentMode === "every" ? (
                  <Field>
                    <Label>Participant</Label>
                    <Select
                      value={everyYearParticipant || ""}
                      onChange={(e) => setEveryYearParticipant(e.target.value)}
                    >
                      <option value="">Select</option>
                      {participants.map((p) => (
                        <option key={p._id} value={p._id}>
                          {p.name}
                        </option>
                      ))}
                    </Select>
                  </Field>
                ) : (
                  <div className="flex gap-2">
                    <Field className="w-1/2">
                      <Label>Even Years</Label>
                      <Select
                        value={evenYearsParticipant || ""}
                        onChange={(e) =>
                          setEvenYearsParticipant(e.target.value)
                        }
                      >
                        <option value="">Select</option>
                        {participants.map((p) => (
                          <option key={p._id} value={p._id}>
                            {p.name}
                          </option>
                        ))}
                      </Select>
                    </Field>
                    <Field className="w-1/2">
                      <Label>Odd Years</Label>
                      <Select
                        value={oddYearsParticipant || ""}
                        onChange={(e) => setOddYearsParticipant(e.target.value)}
                      >
                        <option value="">Select</option>
                        {participants.map((p) => (
                          <option key={p._id} value={p._id}>
                            {p.name}
                          </option>
                        ))}
                      </Select>
                    </Field>
                  </div>
                )}

                <Field>
                  <Label>Notes</Label>
                  <Textarea
                    value={notes}
                    placeholder="Notes"
                    onChange={(e) => setNotes(e.target.value)}
                  />
                </Field>
              </FieldGroup>
            </Fieldset>
          )}
        </DialogBody>
        <DialogActions>
          {step === 1 && (
            <Button plain onClick={() => setIsOpen(false)}>
              Close
            </Button>
          )}
          {step > 1 && (
            <Button
              plain
              onClick={() =>
                setStep((prev) => (prev > 1 ? ((prev - 1) as 1 | 2 | 3) : prev))
              }
            >
              Back
            </Button>
          )}
          {step > 1 && (
            <Button
              disabled={isSaving}
              onClick={
                step < 3 ? () => setStep((step + 1) as 1 | 2 | 3) : handleSave
              }
            >
              {step < 3 ? "Next" : "Create Holiday Rule"}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CreateHolidayRuleButton;
